import { ZoomMtg } from "@zoom/meetingsdk";
import type { ZoomJoinMeetingCommandPayload } from "@repo/zoom-shared";

const ZOOM_LANGUAGE_CODES = [
  "en-US",
  "de-DE",
  "es-ES",
  "fr-FR",
  "ja-JP",
  "jp-JP",
  "pt-PT",
  "pl-PL",
  "tr-TR",
  "ru-RU",
  "zh-CN",
  "zh-TW",
  "ko-KR",
  "ko-KO",
  "vi-VN",
  "it-IT",
  "id-ID",
  "nl-NL",
  "sv-SE",
] as const;

type ZoomLanguage = (typeof ZOOM_LANGUAGE_CODES)[number];

const SUPPORTED_LANGUAGES = new Set<ZoomLanguage>(ZOOM_LANGUAGE_CODES);
const DEFAULT_LANGUAGE: ZoomLanguage = "en-US";
const SDK_JS_LIB_VERSION = "4.0.7";
const ZOOM_JS_LIB_ROOT = `https://source.zoom.us/${SDK_JS_LIB_VERSION}/lib`;

let isPrepared = false;
let activeLanguage: ZoomLanguage = DEFAULT_LANGUAGE;

const isSupportedLanguage = (value?: string): value is ZoomLanguage =>
  typeof value === "string" && SUPPORTED_LANGUAGES.has(value as ZoomLanguage);

const normalizeLanguage = (value?: string): ZoomLanguage =>
  isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE;

const computeDefaultLeaveUrl = (): string => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.href || window.location.origin || "/";
  }

  return "/";
};

const prepareClient = () => {
  if (isPrepared) {
    return;
  }

  ZoomMtg.setZoomJSLib(ZOOM_JS_LIB_ROOT, "/av");
  ZoomMtg.preLoadWasm();
  ZoomMtg.prepareWebSDK();
  isPrepared = true;
};

const ensureLanguageLoaded = async (language?: string) => {
  const targetLanguage = normalizeLanguage(language);

  if (targetLanguage === activeLanguage) {
    return;
  }

  try {
    await ZoomMtg.i18n.load(targetLanguage);
    ZoomMtg.i18n.reload(targetLanguage);
    activeLanguage = targetLanguage;
  } catch (error) {
    console.error(
      `[ZoomClient] Failed to load language resources for "${targetLanguage}".`,
      error
    );

    if (targetLanguage !== DEFAULT_LANGUAGE) {
      await ensureLanguageLoaded(DEFAULT_LANGUAGE);
    }
  }
};

type ZoomMethodArgs = {
  success?: Function;
  error?: Function;
};

const wrapZoomCall = <TArgs extends ZoomMethodArgs>(
  fn: (args: TArgs) => void,
  args: Omit<TArgs, keyof ZoomMethodArgs>
) =>
  new Promise<void>((resolve, reject) => {
    fn({
      ...(args as TArgs),
      success: () => resolve(),
      error: (error: unknown) => reject(error),
    } as TArgs);
  });

export interface ZoomClientInitOptions {
  language?: string;
  leaveUrl?: string;
}

export interface ZoomClient {
  init: (options?: ZoomClientInitOptions) => Promise<void>;
  join: (payload: ZoomJoinMeetingCommandPayload) => Promise<void>;
  leaveMeeting: () => Promise<void>;
  endMeeting: () => Promise<void>;
}

export const createZoomClient = (): ZoomClient => {
  prepareClient();

  return {
    async init(options = {}) {
      await ensureLanguageLoaded(options.language);

      await wrapZoomCall<Parameters<typeof ZoomMtg.init>[0]>(ZoomMtg.init, {
        leaveUrl: options.leaveUrl ?? computeDefaultLeaveUrl(),
        disableCORP: true,
        isSupportAV: true,
      });
    },

    async join(payload) {
      await wrapZoomCall<Parameters<typeof ZoomMtg.join>[0]>(ZoomMtg.join, {
        meetingNumber: payload.meetingNumber,
        userName: payload.userName,
        userEmail: payload.userEmail,
        passWord: payload.password,
        sdkKey: payload.sdkKey,
        customerKey: payload.customerKey,
        tk: payload.tk,
        zak: payload.zak,
        signature: payload.signature,
      });
    },

    leaveMeeting() {
      return wrapZoomCall<Parameters<typeof ZoomMtg.leaveMeeting>[0]>(
        ZoomMtg.leaveMeeting,
        {
          confirm: false,
        }
      );
    },

    endMeeting() {
      return wrapZoomCall<Parameters<typeof ZoomMtg.endMeeting>[0]>(
        ZoomMtg.endMeeting,
        {}
      );
    },
  };
};

export const destroyZoomClient = () => {
  // Client View exposes a singleton, so there is no explicit destroy hook.
};
